import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import QRCode from "qrcode";
import { Share, PlusSquare, Check, Smartphone, Tent, Flame, Wrench, CreditCard, FileText, Download } from "lucide-react";
import { useInstall, track } from "./useInstall";
import { SITE_URL } from "../../lib/site";

export function IosGuide({ onClose }) {
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 flex items-end sm:items-center justify-center p-3" onClick={onClose} data-testid="ios-install-guide">
      <div className="rl-card p-6 w-full max-w-sm" onClick={e => e.stopPropagation()}>
        <div className="pill-badge pill-amber mb-3">IPHONE E IPAD</div>
        <h3 className="font-display font-black text-xl mb-4">Aggiungi Rally Love alla schermata Home</h3>
        <ol className="space-y-3 text-sm">
          {[[Share, "Premi il pulsante Condividi di Safari (il quadrato con la freccia in alto)."], [PlusSquare, "Seleziona “Aggiungi alla schermata Home”."], [Check, "Conferma premendo “Aggiungi”."]].map(([I, t], i) => <li key={i} className="flex gap-3 items-start"><span className="w-8 h-8 rounded-lg bg-white/10 flex items-center justify-center shrink-0"><I className="w-4 h-4 text-[#FF9F0A]" /></span><span className="pt-1.5">{t}</span></li>)}
        </ol>
        <p className="text-xs text-slate-400 mt-4">In pochi secondi troverai l'icona Rally Love nella schermata Home del tuo iPhone.</p>
        <button className="btn-crimson w-full mt-4" onClick={onClose} data-testid="ios-guide-ok">HO CAPITO</button>
      </div>
    </div>
  );
}

export function InstallCta({ className = "", testid = "install-cta" }) {
  const { installed, canPrompt, prompt, ios, mobile } = useInstall(); const [guide, setGuide] = useState(false); const [qr, setQr] = useState(""); const [showQr, setShowQr] = useState(false); const nav = useNavigate();
  useEffect(() => { if (showQr && !qr) QRCode.toDataURL(SITE_URL, { width: 200, margin: 1 }).then(setQr); }, [showQr]);
  if (installed) return <button className={`btn-crimson ${className}`} onClick={() => nav("/community")} data-testid={`${testid}-open`}>APRI RALLY LOVE</button>;
  const click = async () => {
    if (ios) { track("install_click"); setGuide(true); return; }
    if (canPrompt) { await prompt(); return; }
    if (!mobile) { track("install_click"); setShowQr(true); return; }
    track("install_click"); setGuide(true);
  };
  return (
    <>
      <button className={`btn-crimson flex items-center justify-center gap-2 ${className}`} onClick={click} data-testid={testid}><Download className="w-4 h-4" />{mobile ? "INSTALLA RALLY LOVE" : "INSTALLA SUL COMPUTER"}</button>
      {guide && <IosGuide onClose={() => setGuide(false)} />}
      {showQr && <div className="fixed inset-0 z-[70] bg-black/80 flex items-center justify-center p-3" onClick={() => setShowQr(false)} data-testid="desktop-qr-modal"><div className="rl-card p-6 text-center max-w-sm" onClick={e => e.stopPropagation()}><div className="font-display font-bold mb-3">Apri rallylove.it dal tuo smartphone per installare l'app</div>{qr && <img src={qr} alt="QR rallylove.it" className="w-48 h-48 mx-auto rounded bg-white p-1" />}<div className="font-mono-rl text-xs text-slate-400 mt-2">https://rallylove.it</div><button className="btn-outline-rl w-full mt-4" onClick={() => setShowQr(false)}>CHIUDI</button></div></div>}
    </>
  );
}

export function PhoneMockup() {
  const apps = [[Tent, "Paddock"], [Flame, "Daily Stage"], [Wrench, "Rally Farm"], [CreditCard, "Card"], [FileText, "PROMAX"]];
  return (
    <div className="mx-auto w-[240px] h-[480px] rounded-[36px] border-2 border-white/15 bg-[#0B0D14] p-3 relative shadow-[0_0_60px_rgba(255,30,39,0.15)]" data-testid="phone-mockup" aria-hidden="true">
      <div className="absolute top-3 left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-black" />
      <div className="h-full rounded-[26px] bg-[#050505] pt-10 px-4">
        <div className="text-[9px] font-mono-rl text-slate-500 mb-4 text-center">RALLY LOVE</div>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex flex-col items-center gap-1"><img src="/icon-192.png" alt="" className="w-12 h-12 rounded-xl" /><span className="text-[9px] text-slate-300">Rally Love</span></div>
          {apps.map(([I, l]) => <div key={l} className="flex flex-col items-center gap-1"><span className="w-12 h-12 rounded-xl bg-[#141826] border border-white/10 flex items-center justify-center"><I className="w-5 h-5 text-[#FF1E27]" /></span><span className="text-[9px] text-slate-300">{l}</span></div>)}
        </div>
      </div>
    </div>
  );
}

export default function InstallSection() {
  useEffect(() => { track("section_view"); }, []);
  return (
    <section className="py-20 md:py-28 bg-[#0B0D14] border-y border-white/5" data-testid="install-section">
      <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="pill-badge mb-4 inline-flex"><Smartphone className="w-3 h-3" />APP</div>
          <h2 className="headline-huge text-3xl md:text-5xl mb-3">Porta Rally Love sempre con te</h2>
          <p className="text-base md:text-lg text-slate-400 mb-8 max-w-xl">Installa Rally Love sul tuo smartphone e accedi subito a Paddock, giochi, notifiche e documenti ufficiali.</p>
          <div className="flex flex-col sm:flex-row gap-3 items-start"><InstallCta className="min-h-[48px]" testid="home-install-btn" /><a href="/install" className="btn-outline-rl min-h-[48px] flex items-center" data-testid="home-install-more">COME FUNZIONA</a></div>
          <p className="text-xs text-slate-500 mt-4">Installazione gratuita. Nessun passaggio obbligatorio dagli store.</p>
        </div>
        <PhoneMockup />
      </div>
    </section>
  );
}
